import React, { forwardRef, useCallback } from 'react'
import type { CSSObject } from '@emotion/react'

import {
  KitElement,
  attachPlatformMethods,
  composeGroups,
  flexItem,
  paddings,
  resolveStyleProps,
  spaceStyles,
  toDOMProps,
  typography,
  useLayoutHandler,
  useMergedRef,
  type KitViewProps,
  type RNStyle
} from '../../_support'

/* -----------------------------------------------------------------------------
 * NativeTextInput
 *
 * react-native's TextInput as react-native-web 0.17 rendered it: an <input>, or
 * a <textarea> when multiline, with its reset, onChangeText, onSubmitEditing on
 * Enter, onKeyPress, keyboardType and secureTextEntry. Takes the style props the
 * kit's TextInput gave it (padding, `space` margins, typography, flex).
 * -------------------------------------------------------------------------- */

export const nativeTextInputStyles = composeGroups(
  paddings,
  spaceStyles,
  typography,
  flexItem
)

const TEXT_INPUT_BASE: RNStyle = {
  MozAppearance: 'textfield',
  WebkitAppearance: 'none',
  backgroundColor: 'transparent',
  borderWidth: 0,
  borderStyle: 'solid',
  borderColor: 'black',
  borderRadius: 0,
  boxSizing: 'border-box',
  font: '14px System',
  margin: 0,
  padding: 0,
  resize: 'none'
}

const keyboardTypes: Record<string, { type?: string; inputMode?: string }> = {
  'email-address': { type: 'email' },
  'number-pad': { inputMode: 'numeric' },
  numeric: { inputMode: 'numeric' },
  'decimal-pad': { inputMode: 'decimal' },
  'phone-pad': { type: 'tel' },
  search: { type: 'search' },
  'web-search': { type: 'search' },
  url: { type: 'url' }
}

// the only extra props react-native-web's TextInput forwarded
const FORWARDED_INPUT = [
  'autoFocus',
  'defaultValue',
  'disabled',
  'maxLength',
  'placeholder',
  'value'
]

export type NativeTextInputProps = KitViewProps & {
  value?: string
  defaultValue?: string
  placeholder?: string
  placeholderTextColor?: string
  multiline?: boolean
  numberOfLines?: number
  editable?: boolean
  secureTextEntry?: boolean
  keyboardType?: string
  returnKeyType?: string
  autoCapitalize?: string
  autoCorrect?: boolean
  autoComplete?: string
  autoCompleteType?: string
  spellCheck?: boolean
  maxLength?: number
  blurOnSubmit?: boolean
  onChangeText?: (text: string) => void
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void
  onSubmitEditing?: (event: React.KeyboardEvent) => void
  onKeyPress?: (event: React.KeyboardEvent) => void
  onFocus?: (event: React.FocusEvent) => void
  onBlur?: (event: React.FocusEvent) => void
  // useField's autofill detection: react-native-web dropped it, so labels sat
  // on top of values the browser filled in
  onAnimationStart?: (event: React.AnimationEvent) => void
}

const NativeTextInput = forwardRef<HTMLInputElement, NativeTextInputProps>(
  (props, ref) => {
    const {
      autoCapitalize = 'sentences',
      autoComplete,
      autoCompleteType,
      autoCorrect = true,
      blurOnSubmit,
      editable = true,
      keyboardType = 'default',
      multiline = false,
      numberOfLines = 1,
      onAnimationStart,
      onBlur,
      onChange,
      onChangeText,
      onFocus,
      onKeyPress,
      onLayout,
      onSubmitEditing,
      placeholderTextColor,
      returnKeyType,
      secureTextEntry = false,
      spellCheck,
      // react-native only: nothing on the web
      textContentType,
      underlineColorAndroid,
      selectionColor,
      ...rest
    } = props as NativeTextInputProps & Record<string, any>

    const layoutRef = useLayoutHandler(onLayout)
    const mergedRef = useMergedRef(ref, layoutRef, attachPlatformMethods)

    const inputProps: Record<string, any> = {}
    FORWARDED_INPUT.forEach(key => {
      if (key in rest) {
        inputProps[key] = rest[key]
        delete rest[key]
      }
    })

    const { css, rest: forwarded } = resolveStyleProps(
      rest,
      nativeTextInputStyles,
      {
        base: TEXT_INPUT_BASE
      }
    )
    const { domProps, pointerStyle } = toDOMProps(forwarded)
    const placeholderStyle = placeholderTextColor
      ? { '&::placeholder': { color: placeholderTextColor, opacity: 1 } }
      : {}

    const handleChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        onChange?.(event)
        onChangeText?.(event.target.value)
      },
      [onChange, onChangeText]
    )

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      // react-native-web kept key events from bubbling
      event.stopPropagation()
      onKeyPress?.(event)
      if (
        event.key === 'Enter' &&
        !event.shiftKey &&
        !(event.nativeEvent as KeyboardEvent).isComposing &&
        !event.isDefaultPrevented()
      ) {
        const shouldBlur = blurOnSubmit == null ? !multiline : blurOnSubmit
        if ((blurOnSubmit || !multiline) && onSubmitEditing) {
          event.preventDefault()
          onSubmitEditing(event)
        }
        if (shouldBlur) event.currentTarget.blur()
      }
    }

    const { type: keyboardInputType = 'text', inputMode } =
      keyboardTypes[keyboardType] ?? {}

    return (
      <KitElement
        as={multiline ? 'textarea' : 'input'}
        ref={mergedRef}
        kitCss={{ ...css, ...pointerStyle, ...placeholderStyle } as CSSObject}
        {...domProps}
        {...inputProps}
        type={
          multiline
            ? undefined
            : secureTextEntry
            ? 'password'
            : keyboardInputType
        }
        inputMode={inputMode}
        rows={multiline ? numberOfLines : undefined}
        readOnly={!editable}
        autoCapitalize={autoCapitalize}
        autoComplete={autoComplete || autoCompleteType || 'on'}
        autoCorrect={autoCorrect ? 'on' : 'off'}
        spellCheck={spellCheck ?? autoCorrect}
        dir={domProps.dir ?? 'auto'}
        {...(returnKeyType ? { enterKeyHint: returnKeyType } : {})}
        onChange={handleChange}
        onFocus={onFocus}
        onBlur={onBlur}
        onAnimationStart={onAnimationStart}
        onKeyDown={handleKeyDown}
      />
    )
  }
)
NativeTextInput.displayName = 'NativeTextInput'

export default NativeTextInput
